import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import PendingIcon from '@mui/icons-material/Pending';
import RouteIcon from '@mui/icons-material/Route';
import MapIcon from '@mui/icons-material/Map';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import DashboardMapHoy from '../components/DashboardMapHoy';

function TablaSinItinerario({ rows, loading, onRowClick }) {
  const organizacion = useAuthStore((state) => state.user?.organizacion);
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        Sin registros
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Servicio</TableCell>
            <TableCell align="right">Cantidad</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <Tooltip key={row.Servicio} title="Ver en itinerarios" placement="left">
              <TableRow
                hover
                onClick={() => onRowClick({ servicio: String(row.Servicio) })}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {row.Imagen && (
                      <Box
                        component="img"
                        src={`/uploads/${organizacion}/${row.Imagen}`}
                        alt={row.NombreServicio}
                        sx={{ width: 24, height: 24, objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {row.NombreServicio}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    {row.Atrasado && (
                      <Chip label="Atrasado" size="small" color="error" />
                    )}
                    <Chip label={row.cantidad} size="small" color="warning" />
                  </Box>
                </TableCell>
              </TableRow>
            </Tooltip>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const formatFecha = (fecha) => {
  if (!fecha) return '-';
  const [y, m, d] = fecha.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

function TablaConItinerario({ rows, loading, onRowClick }) {
  const organizacion = useAuthStore((state) => state.user?.organizacion);
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        Sin registros
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Servicio</TableCell>
            <TableCell>Fecha</TableCell>
            <TableCell align="right">Cantidad</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <Tooltip key={`${row.Servicio}-${row.Fecha}`} title="Ver itinerario" placement="left">
              <TableRow
                hover
                onClick={() => onRowClick({ servicio: String(row.Servicio), fecha: row.Fecha })}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {row.Imagen && (
                      <Box
                        component="img"
                        src={`/uploads/${organizacion}/${row.Imagen}`}
                        alt={row.NombreServicio}
                        sx={{ width: 24, height: 24, objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {row.NombreServicio}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {formatFecha(row.Fecha)}
                    {row.Atrasado && (
                      <Chip label="Atrasado" size="small" color="error" />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Chip label={row.cantidad} size="small" color="info" />
                </TableCell>
              </TableRow>
            </Tooltip>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-resumen'],
    queryFn: async () => {
      const res = await dashboardAPI.resumen();
      return res.data.data;
    },
    refetchInterval: 60000,
  });

  const sinItinerario = data?.sinItinerario ?? [];
  const conItinerario = data?.conItinerario ?? [];

  const totalSinItinerario = sinItinerario.reduce((acc, r) => acc + r.cantidad, 0);
  const totalConItinerario = conItinerario.reduce((acc, r) => acc + r.cantidad, 0);

  const handleRowClick = (state) => {
    navigate('/recolecciones', { state });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Pendientes sin itinerario */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PendingIcon color="warning" />
              <Typography variant="h6">Pendientes de itinerario</Typography>
              {!isLoading && (
                <Chip label={totalSinItinerario} color="warning" size="small" sx={{ ml: 'auto' }} />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Recolecciones pedidas sin itinerario asignado
            </Typography>
            <TablaSinItinerario rows={sinItinerario} loading={isLoading} onRowClick={handleRowClick} />
          </Paper>
        </Grid>

        {/* Con itinerario, pendientes de recolección */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <RouteIcon color="info" />
              <Typography variant="h6">Con itinerario, pendientes de recolección</Typography>
              {!isLoading && (
                <Chip label={totalConItinerario} color="info" size="small" sx={{ ml: 'auto' }} />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Recolecciones en itinerario aún no recolectadas
            </Typography>
            <TablaConItinerario rows={conItinerario} loading={isLoading} onRowClick={handleRowClick} />
          </Paper>
        </Grid>

        {/* Mapa de recolecciones de hoy */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <MapIcon color="primary" />
              <Typography variant="h6">Recolecciones de hoy</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Recorrido de los itinerarios de hoy y de los atrasados con recolecciones pendientes, marcando puntos recolectados y pendientes
            </Typography>
            <DashboardMapHoy />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
